import { Injectable } from '@angular/core';
import { Environment } from 'src/environments/environment';
import { GlobalConstService } from './global-const.service';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { FileService } from './file.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PlatformService } from './platform.service';
import { PromptCameraService } from './prompt-camera.service';
import { DocumentPickerCapacitorService } from './document-picker-capacitor.service';

@Injectable({ providedIn: 'root' })
export class SurveyService {
  public surveyInitConfig;

  public images$: BehaviorSubject<any> = new BehaviorSubject([]);
  public documents$: BehaviorSubject<any> = new BehaviorSubject([]);

  constructor(
    private httpService: HttpService,
    private fileService: FileService,
    private httpClient: HttpClient,
    private platformService: PlatformService,
    private promptCameraService: PromptCameraService,
    private documentPickerCapacitor: DocumentPickerCapacitorService
  ) {}

  public getSurvey(idPage: number) {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/form/${idPage}/elements`);
  }

  public sendSurvey(idPage: number, formResponses): Observable<any> {
    const formData = new FormData();

    formResponses.forEach((response, index) => {
      if (response.answers && response.answers.length > 0) {
        response.answers.forEach((answer, answerIndex) => {
          
          if (answer && answer.blob instanceof Blob) {
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer.blob, answer.name);
          } else if (answer !== null && typeof answer === 'object' && answer.blob && answer.blob instanceof Blob) {
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer.blob, answer.name || `response_${response.id}`);
          } else if (answer !== null && answer instanceof Blob) {
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer, `response_${response.id}`);
          } else if (answer !== null && typeof answer === 'object') {
            Object.keys(answer).forEach((key) => {
              const value = answer[key];
              const fieldName = `${response.id}.${key}`;

              if (value instanceof File) {
                formData.append(fieldName, value, value.name);
              } else {
                formData.append(fieldName, JSON.stringify(value));
              }
            });
          } else if (answer !== null && answer !== undefined) {
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer);
          }
        });
      }
    });

    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/form/${idPage}/submit`, formData);
  }

  public resetUploadedFiles(): void {
    this.images$.next([]);
    this.documents$.next([]);
  }

  public isQuestionMandatory(question: any): boolean {
    const mandatory = question?.mandatory;
    return mandatory === true || mandatory === 1 || mandatory === '1' || mandatory === 'true';
  }

  private hasValidImageData(image: any): boolean {
    if (!image) {
      return false;
    }

    return !!(image.blob || image.path || image.webPath || image.image);
  }

  private hasValidDocumentData(document: any): boolean {
    if (!document) {
      return false;
    }

    return !!(document.blob || document.path || document.webPath || document.name || document.document_name);
  }

  public isImageQuestionFilled(question: any, form?: any): boolean {
    const images = this.images$.value;
    const documents = this.documents$.value;

    const imageEntry = images.find((img) => img.questionId == question.id);
    if (this.hasValidImageData(imageEntry?.image)) {
      return true;
    }

    const documentEntry = documents.find((doc) => doc.questionId == question.id);
    if (this.hasValidDocumentData(documentEntry?.document)) {
      return true;
    }

    const control = form?.get?.(question.question);
    const value = control?.value;

    if (value instanceof Blob || value instanceof File) {
      return true;
    }

    if (value && typeof value === 'object') {
      return this.hasValidImageData(value) || this.hasValidDocumentData(value);
    }

    return false;
  }

  /** @deprecated Use isImageQuestionFilled instead */
  public isRequiredImageQuestionFilled(question: any, form: any): boolean {
    return this.isImageQuestionFilled(question, form);
  }

  public async prepareUserResponse(values, surveyFormDataResponse) {
    const listOfResponses = [];

    for (const question of surveyFormDataResponse) {
      const response = await this.setUserResponse(values, question);
      
      listOfResponses.push({
        id: question.id,
        answers: response
      });
    }
    
    return listOfResponses;
  }

  private async setUserResponse(values, question) {
    const keysResponse = Object.keys(values);
    const responseKey = keysResponse.find((res) => res === question.question);

    if (question.inputType === 'image') {
      return this.processImageResponse(values, question, responseKey);
    } else {
      return values[responseKey] !== undefined && values[responseKey] !== null 
        ? [values[responseKey]] 
        : [];
    }
  }

  private async processImageResponse(values, question, responseKey) {
    const images = this.images$.value;
    const documents = this.documents$.value;
    
    const image = images.find((img) => img.questionId == question.id);
    const document = documents.find((doc) => doc.questionId == question.id);

    // Prioridad 1: Blob directo del formulario (web)
    if (values[responseKey] instanceof Blob) {
      return [{
        blob: values[responseKey],
        name: `response_${question.id}`,
        image: null,
        document: null
      }];
    }
    
    // Prioridad 2: Blob del servicio (nativo)
    if (image?.image?.blob) {
      return [{
        blob: image.image.blob,
        name: `response_${question.id}`,
        image: image.image,
        document: null
      }];
    }

    // Prioridad 3: Documento del servicio
    if (document?.document?.blob) {
      return [{
        blob: document.document.blob,
        name: `response_${question.id}`,
        image: null,
        document: document.document
      }];
    }

    // Si no hay datos y el campo no es obligatorio
    if (!question.mandatory && !image && !document) {
      return [];
    }

    // Si no hay datos y el campo es obligatorio
    if (question.mandatory && !image && !document) {
      return [];
    }

    // Procesar imagen si existe (fallback para casos edge)
    if (image) {
      const blob = await this.createBlobFromImage(image);
      if (blob) {
        return [{
          blob: blob,
          name: `response_${question.id}`,
          image: image.image,
          document: null
        }];
      }
    }

    return [];
  }

  private async createBlobFromImage(image) {
    try {
      if (this.platformService.isWeb()) {
        return this.createBlobForWeb(image);
      } else {
        return this.createBlobForNative(image);
      }
    } catch (error) {
      console.error('Error creating blob from image:', error);
      return null;
    }
  }

  private createBlobForWeb(image) {
    if (image.image?.webPath && typeof image.image.webPath === 'string' && image.image.webPath.startsWith('data:')) {
      return this.fileService.base64ToBlob(
        image.image.webPath,
        `image/${image.image.format || 'jpeg'}`
      );
    } else if (image.image?.blob) {
      return image.image.blob;
    } else if (image.image?.webPath && typeof image.image.webPath === 'string') {
      return this.httpClient.get(image.image.webPath, { responseType: 'blob' }).toPromise();
    }
    return null;
  }

  private async createBlobForNative(image) {
    if (image.image?.blob) {
      return image.image.blob;
    } else if (image.image?.path || image.image?.webPath) {
      const base64 = await this.fileService.convertFilePathToBase64(
        image.image.path || image.image.webPath
      );
      if (typeof base64 === 'string') {
        return this.fileService.base64ToBlob(base64, `image/${image.image.format || 'jpeg'}`);
      }
    }
    return null;
  }

  public async uploadCameraPictureWeb(event, form, question, fileImage) {
    if (event.target.files && event.target.files.length) {
      let [file] = event.target.files;
      file = await this.fileService.generateFile(file);

      if (form) {
        form.patchValue({ [question.question]: file });
        // Marcar el control como touched y dirty para que la validación funcione correctamente
        const control = form.get(question.question);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
        }
      }

      this.uploadPicture(file, question.id, form, question); // Associate the image with the question ID
    }
    fileImage.nativeElement.value = '';
  }

  public async selectPicture(fileInput, question, form?) {
    if (this.platformService.isWeb()) {
      fileInput.nativeElement.accept = 'image/*';
      fileInput.nativeElement.click();
      return;
    }
    
    try {
      const result = await this.promptCameraService.ask();
      switch (result.action) {
        case this.promptCameraService.OPEN_INPUT_FILE:
          fileInput.nativeElement.click();
          break;
        case this.promptCameraService.REMOVE_IMAGE:
          break;
        default:
          if (result.image) {
            // Procesar la imagen seleccionada
            await this.uploadPicture(result.image, question.id, form, question);
            
            // Disparar un evento personalizado para notificar al componente
            const customEvent = new CustomEvent('imageUploaded', {
              detail: { questionId: question.id }
            });
            document.dispatchEvent(customEvent);
          }
          break;
      }
    } catch (error) {
      console.error('Error with PromptCameraService:', error);
      fileInput.nativeElement.click();
    }
  }

  public async uploadPicture(image, questionId, form?: any, question?: any) {
    const currentImages = this.images$.value;
    const filteredImages = currentImages.filter((img) => img.questionId !== questionId);
    
    let newImage;
    if (this.platformService.isWeb() && image && image.blob && image.image) {
      newImage = { 
        questionId, 
        image: {
          webPath: image.image,
          path: image.image,
          format: image.name ? image.name.split('.').pop() : 'jpeg',
          name: image.name,
          blob: image.blob // Asegurar que el blob se incluya
        }
      };
      if (form && question) {
        const control = form.get(question.question);
        if (control) {
          control.setValue(image.blob);
          control.markAsTouched();
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      }
    } else {
      if (image && (image.path || image.webPath)) {
        try {
          const base64: any = await this.fileService.convertFilePathToBase64(image.path || image.webPath);
          // Crear el blob directamente desde el base64 puro
          const blob = this.fileService.base64ToBlob(base64, `image/${image.format || 'jpeg'}`);
          
          newImage = { 
            questionId, 
            image: {
              ...image,
              blob: blob // Agregar el blob al objeto de imagen
            }
          };
          if (form && newImage.image.blob && question) {
            const control = form.get(question.question);
            if (control) {
              control.setValue(newImage.image.blob);
              control.markAsTouched();
              control.markAsDirty();
              control.updateValueAndValidity();
            }
          }
        } catch (error) {
          console.error('Error creating blob for native image:', error);
          // Fallback: guardar sin blob
          newImage = { questionId, image };
        }
      } else {
        newImage = { questionId, image };
      }
    }
    const updatedImages = [...filteredImages, newImage];
    this.images$.next(updatedImages);
    const currentDocuments = this.documents$.value;
    const filteredDocuments = currentDocuments.filter((doc) => doc.questionId !== questionId);
    this.documents$.next(filteredDocuments);
  }

  public async uploadDocumentWeb(event, form, question, fileDocument) {
    if (event.target.files && event.target.files.length) {
      let [file] = event.target.files;
      file = await this.fileService.generateDocumentFile(file);

      if (form) {
        form.patchValue({ [question.question]: file });
        // Marcar el control como touched y dirty para que la validación funcione correctamente
        const control = form.get(question.question);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
        }
      }

      this.uploadDocument(file, question.id, form, question); // Associate the document with the question ID
    }
    fileDocument.nativeElement.value = '';
  }

  public async selectDocument(fileInput, question, form?) {
    if (this.platformService.isWeb()) {
      
      // Crear un action sheet simple para web
      const actionSheet = document.createElement('div');
      actionSheet.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        z-index: 9999;
        display: flex;
        align-items: flex-end;
      `;
      
      const content = document.createElement('div');
      content.style.cssText = `
        background: white;
        width: 100%;
        border-radius: 16px 16px 0 0;
        padding: 20px;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.2);
      `;
      
      const options = [
        { text: 'Seleccionar imagen', action: 'select_image' },
        { text: 'Seleccionar documento', action: 'select_document' },
        { text: 'Cancelar', action: 'cancel' }
      ];
      
      options.forEach(option => {
        const button = document.createElement('button');
        button.textContent = option.text;
        button.style.cssText = `
          width: 100%;
          padding: 16px;
          margin: 8px 0;
          border: none;
          background: ${option.action === 'cancel' ? '#f0f0f0' : 'white'};
          color: ${option.action === 'cancel' ? '#666' : '#000'};
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        `;
        
        button.onclick = async () => {
          document.body.removeChild(actionSheet);
          
          if (option.action === 'select_image') {
            fileInput.nativeElement.accept = 'image/*';
            
            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                try {
                  const processedFile = await this.fileService.generateFile(file);
                  await this.uploadPicture(processedFile, question.id, form, question);
                  document.dispatchEvent(new CustomEvent('imageUploaded', { detail: { questionId: question.id } }));
                } catch (error) {
                  console.error('Error processing image:', error);
                  await this.uploadPicture(file, question.id, form, question);
                  document.dispatchEvent(new CustomEvent('imageUploaded', { detail: { questionId: question.id } }));
                }
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };
            
            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          } else if (option.action === 'select_document') {
            fileInput.nativeElement.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,.ppt,.pptx';
            
            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                if (file.type.startsWith('image/')) {
                  console.warn('Image files are not allowed in document selection');
                  fileInput.nativeElement.removeEventListener('change', handleFileChange);
                  return;
                }
                try {
                  const processedFile = await this.fileService.generateDocumentFile(file);
                  await this.uploadDocument(processedFile, question.id, form, question);
                  document.dispatchEvent(new CustomEvent('documentUploaded', { detail: { questionId: question.id } }));
                } catch (error) {
                  console.error('Error processing document:', error);
                  await this.uploadDocument(file, question.id, form, question);
                  document.dispatchEvent(new CustomEvent('documentUploaded', { detail: { questionId: question.id } }));
                }
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };
            
            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          }
        };
        
        content.appendChild(button);
      });
      
      actionSheet.appendChild(content);
      document.body.appendChild(actionSheet);
      
      // Cerrar al hacer clic fuera del action sheet
      actionSheet.onclick = (e) => {
        if (e.target === actionSheet) {
          document.body.removeChild(actionSheet);
        }
      };
      
      return;
    }
    try {
      const result = await this.promptCameraService.show(false, true);
      
      switch (result.action) {
        case 'take_photo':
        case 'select_photo':
          if (result.image) {
            await this.uploadPicture(result.image, question.id, form, question);
            document.dispatchEvent(new CustomEvent('imageUploaded', { detail: { questionId: question.id } }));
          }
          break;
        case this.promptCameraService.SELECT_DOCUMENT_MOBILE:
          if (result.image?.document) {
            const doc = this.fileService.ensureDocumentBlob(result.image.document);
            await this.uploadDocument(doc, question.id, form, question);
            document.dispatchEvent(new CustomEvent('documentUploaded', { detail: { questionId: question.id } }));
          }
          break;
        case this.promptCameraService.OPEN_INPUT_FILE:
          try {
            const documentResult = await this.documentPickerCapacitor.pickDocument();
            if (documentResult && documentResult.success && documentResult.document) {
              await this.uploadDocument(documentResult.document, question.id, form, question);
              document.dispatchEvent(new CustomEvent('documentUploaded', { detail: { questionId: question.id } }));
            }
          } catch (error) {
            if (error?.message === 'pickFiles canceled.') {
              break;
            }
            console.error('Error with DocumentPickerCapacitorService:', error);
            fileInput.nativeElement.accept = '.pdf,.doc,.docx,.txt,.rtf,.odt,.ods,.odp,.xls,.xlsx,.ppt,.pptx,.csv';

            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                if (!file.type.startsWith('image/')) {
                  try {
                    const processedFile = await this.fileService.generateDocumentFile(file);
                    await this.uploadDocument(processedFile, question.id, form, question);
                  } catch (err) {
                    console.error('Error processing document:', err);
                    await this.uploadDocument(file, question.id, form, question);
                  }
                }
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };

            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error with PromptCameraService:', error);
      fileInput.nativeElement.click();
    }
  }

  public async selectPictureWithDocuments(fileImageInput, fileDocumentInput, question, form?) {
    if (this.platformService.isWeb()) {
      fileDocumentInput.nativeElement.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,*/*';
      fileDocumentInput.nativeElement.click();
      return;
    }
    fileDocumentInput.nativeElement.click();
  }

  public async uploadDocument(document, questionId, form?: any, question?: any) {
    const currentDocuments = this.documents$.value;
    // Remover documento anterior si existe para esta pregunta
    const filteredDocuments = currentDocuments.filter((doc) => doc.questionId !== questionId);
    
    let newDocument;
    
    if (document && document.blob) {
      newDocument = { questionId, document };
      if (form && question) {
        const control = form.get(question.question);
        if (control) {
          control.setValue(document.blob);
          control.markAsTouched();
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      }
    } else if (document && (document.path || document.webPath)) {
      try {
        if (!document.blob) {
          const base64: any = await this.fileService.convertFilePathToBase64(document.path || document.webPath);
          const blob = this.fileService.base64ToBlob(base64, document.type || 'application/octet-stream');
          document.blob = blob;
        }
        newDocument = { questionId, document };
        if (form && document.blob && question) {
          const control = form.get(question.question);
          if (control) {
            control.setValue(document.blob);
            control.markAsTouched();
            control.markAsDirty();
            control.updateValueAndValidity();
          }
        }
      } catch (error) {
        console.error('Error creating blob for native document:', error);
        newDocument = { questionId, document };
      }
    } else {
      newDocument = { questionId, document };
    }
    const updatedDocuments = [...filteredDocuments, newDocument];
    this.documents$.next(updatedDocuments);
    const currentImages = this.images$.value;
    const filteredImages = currentImages.filter((img) => img.questionId !== questionId);
    this.images$.next(filteredImages);
  }

}
